/**
 * SubscriptionPill — small clickable pill showing subscription status.
 * Used in the mobile top-bar of both the public Header and the members
 * area layout. Fetches /api/members/me when `plan` isn't passed in.
 *
 * Colors:
 *   active     → emerald
 *   trial      → amber (with hour countdown)
 *   course_only → brand
 *   default    → surface (invites the user to join)
 */
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPill = ({ plan: planProp, to = '/members/billing', testid = 'mobile-subscription-pill', authUser }) => {
  const [plan, setPlan] = useState(planProp || null);

  useEffect(() => {
    if (planProp !== undefined) return undefined;
    if (!authUser) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/members/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setPlan(d?.plan || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [planProp, authUser]);

  const isActive = plan?.is_active;
  const isTrial = plan?.is_trial;
  const isCourseOnly = plan?.course_only;
  let label = 'הצטרפו למועדון';
  let cls = 'bg-surface-100 text-surface-700';
  let dotCls = 'bg-surface-400';
  if (isActive) {
    label = 'מנוי פעיל';
    cls = 'bg-emerald-50 text-emerald-700';
    dotCls = 'bg-emerald-500';
  } else if (isTrial) {
    const hoursLeft = plan?.seconds_left ? Math.max(1, Math.round(plan.seconds_left / 3600)) : 24;
    label = `טריאל · ${hoursLeft}ש׳`;
    cls = 'bg-amber-50 text-amber-700';
    dotCls = 'bg-amber-500';
  } else if (isCourseOnly) {
    label = 'רכשתם קורס';
    cls = 'bg-brand-50 text-brand-700';
    dotCls = 'bg-brand-600';
  }
  return (
    <NavLink
      to={to}
      className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-colors ${cls}`}
      data-testid={testid}
      aria-label={`מצב מנוי: ${label}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls} flex-shrink-0`} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
};

export default SubscriptionPill;
